import React, {useCallback, useContext, useRef, useState} from 'react';
import {Formik} from 'formik';
import {MY_COVERY_COOKIE} from 'constants/url.constants';
import {COVERY_DOMAIN} from 'constants/domain.constants';
import ErrorBoundary from 'modules/boundary/components/ErrorBoundary';
import {CookieContext} from '../cookie.context';
import {setCookie} from '../helpers/setCookie';
import {deleteCookie} from '../helpers/deleteCookie';
import {getCookie} from '../helpers/getCookie';
import {COOKIE_POLICY_ACCEPTED, COOKIE_POLICY_USER_ID, STATISTIC_SCRIPTS_ADDED, ADVERTISING_SCRIPTS_ADDED} from '../cookie.constants';
import {PageCookie as PageCookieView} from '../components/PageCookie';
import {PageWrapper} from '../components/PageWrapper';
import {PageCheckboxList} from '../components/PageCheckboxList';
import {ButtonsContainer} from '../components/ButtonsContainer';
import { useRecaptcha } from './RecaptchaProvider';

export function PageCookie(props) {
	const {
		list,
		checkboxes,
	} = props;

	const providerRecaptcha = useRecaptcha();
	const crypto = typeof window !== 'undefined' ? window.crypto.randomUUID() : '';
	const userIdRef = useRef(getCookie(COOKIE_POLICY_USER_ID) || crypto);
	const userId = userIdRef.current;
	const [isLoading, setIsLoading] = useState(false);
	const formRef = useRef(null);
	const {setStatistics, setAdvertising} = useContext(CookieContext);

	const setEssentialScripts = useCallback(() => {
		const expirationDate = new Date();
		expirationDate.setFullYear(expirationDate.getFullYear() + 1);
		setCookie(COOKIE_POLICY_ACCEPTED, JSON.stringify(true), expirationDate, COVERY_DOMAIN);
		setCookie(COOKIE_POLICY_USER_ID, userId, expirationDate, COVERY_DOMAIN);
	}, [userId]);

	const createHandleSubmit = () => {
		const values = formRef.current.values;
		if (values.cookie.includes('essential')) {
			setEssentialScripts();
		}
		if (values.cookie.includes('advertising')) {
			setAdvertising();
		} else {
			deleteCookie(ADVERTISING_SCRIPTS_ADDED, COVERY_DOMAIN);
		}
		if (values.cookie.includes('statistics')) {
			setStatistics();
		} else {
			deleteCookie(STATISTIC_SCRIPTS_ADDED, COVERY_DOMAIN);
		}
		const formData = new FormData();
		Object.keys(values).forEach((key) => {
			formData.append(key, values[key]);
		});

		fetch(MY_COVERY_COOKIE, {
			method: 'POST',
			body: formData,
			mode: 'cors',
		})
			.then((response) => response.json())
			.finally(() => {
				setIsLoading(false);
				window.location.href = '/#intro';
			});
	};

	const handleUserAction = (userId) => (async () => {
		providerRecaptcha?.resetCaptcha();
		await new Promise((resolve) => setTimeout(resolve, 500));
		const verifiedToken = await providerRecaptcha?.executeRecaptcha();
		if (!verifiedToken) return;
		formRef.current.setFieldValue('g-recaptcha-response', verifiedToken);
		formRef.current.setFieldValue('user_id', userId);
		setTimeout(createHandleSubmit, 0);
	});

	const handleAcceptAllCookie = useCallback(() => {
		if (isLoading) return;
		formRef.current.setFieldValue('cookie', ['essential', 'advertising', 'statistics']);
		setTimeout(() => {
			formRef.current.submitForm();
		}, 0);
	}, [isLoading]);

	const handleAcceptSelected = useCallback(() => {
		if (isLoading) return;
		formRef.current.submitForm();
	}, [isLoading]);

	const handleSubmit = handleUserAction(userId);

	return (
		<PageWrapper>
			<PageCookieView
				list={list}
			>
				<Formik
					initialValues={{
						cookie: ['essential'],
						'g-recaptcha-response': '',
					}}
					onSubmit={handleSubmit}
				>
					{(form) => {
						formRef.current = form;
						return <PageCheckboxList
							form={form}
							checkboxesList={checkboxes}
						/>;
					}}
				</Formik>
			</PageCookieView>
			<ErrorBoundary>
				<ButtonsContainer
					isSettingsOpen
					onAcceptAllCookie={handleAcceptAllCookie}
					onAcceptSelected={handleAcceptSelected}
					isDisabled={isLoading || !providerRecaptcha?.isReady}
				/>
			</ErrorBoundary>
		</PageWrapper>
	);
}
